import {
  getExtensionTexts,
  getGeneratedAnimalName,
  getHandTypeLabel,
  getStageLabel,
} from './catalogs';
import {
  MessageDescriptor,
  MessageKey,
  TranslatableText,
  getGeneralMessages,
} from './messages';
import {
  Locale,
  MessageFormatter,
  MessagePrimitive,
  ResolvedMessageValues,
  SUPPORTED_LOCALES,
} from './types';

const formatTemplate = (
  template: string,
  values: ResolvedMessageValues,
): string => {
  return template.replace(/\{([^}]+)\}/g, (_match, key) => {
    const value = values[key];
    if (typeof value === 'undefined') {
      return '';
    }
    return String(value);
  });
};

const resolveCatalogEntry = (
  locale: Locale,
  key: MessageKey,
): string | MessageFormatter | undefined => {
  const generalMessages = getGeneralMessages(locale);
  if (Object.prototype.hasOwnProperty.call(generalMessages, key)) {
    return generalMessages[key as keyof typeof generalMessages];
  }

  if (key.startsWith('card.')) {
    const [, rawType, field] = key.split('.');
    const type = rawType as Parameters<typeof getExtensionTexts>[1];
    const entry = getExtensionTexts(locale, type);
    if (field === 'shortName') {
      return entry.shortName || entry.name;
    }
    if (field === 'description') {
      return entry.description;
    }
    return entry.name;
  }

  if (key.startsWith('stage.')) {
    const [, rawStage] = key.split('.');
    return getStageLabel(
      locale,
      rawStage as Parameters<typeof getStageLabel>[1],
    );
  }

  if (key.startsWith('handType.')) {
    const [, rawType] = key.split('.');
    return getHandTypeLabel(
      locale,
      rawType as Parameters<typeof getHandTypeLabel>[1],
    );
  }

  if (key.startsWith('animal.generated.')) {
    const [, , rawName] = key.split('.');
    return getGeneratedAnimalName(
      locale,
      rawName as Parameters<typeof getGeneratedAnimalName>[1],
    );
  }

  return undefined;
};

const isMessageDescriptor = (value: unknown): value is MessageDescriptor => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'key' in value &&
    typeof (value as { key?: unknown }).key === 'string'
  );
};

const resolveValue = (
  locale: Locale,
  value: MessagePrimitive | MessageDescriptor,
): string | number | boolean | undefined => {
  if (typeof value === 'undefined' || value === null) {
    return undefined;
  }
  if (isMessageDescriptor(value)) {
    return formatMessage(locale, value);
  }
  return value;
};

export const formatMessage = (
  locale: Locale,
  messageOrKey: MessageDescriptor | MessageKey,
  values?: MessageDescriptor['values'],
): string => {
  const descriptor =
    typeof messageOrKey === 'string'
      ? { key: messageOrKey, values }
      : messageOrKey;
  const entry = resolveCatalogEntry(locale, descriptor.key);
  if (!entry) {
    return descriptor.key;
  }

  const resolvedValues = Object.fromEntries(
    Object.entries(descriptor.values || {}).map(([key, value]) => [
      key,
      resolveValue(locale, value),
    ]),
  ) as ResolvedMessageValues;

  if (typeof entry === 'function') {
    return entry(resolvedValues, locale);
  }

  return formatTemplate(entry, resolvedValues);
};

export const formatText = (locale: Locale, text: TranslatableText): string => {
  return typeof text === 'string' ? text : formatMessage(locale, text);
};

export const normalizeLocale = (value?: string | null): Locale | null => {
  if (!value) {
    return null;
  }

  const normalized = value.toLowerCase().split('-')[0];
  return SUPPORTED_LOCALES.includes(normalized as Locale)
    ? (normalized as Locale)
    : null;
};

export const resolveLocale = (
  preferredLocale?: string | null,
  browserLanguages: readonly string[] = [],
): Locale => {
  const preferred = normalizeLocale(preferredLocale);
  if (preferred) {
    return preferred;
  }

  for (const candidate of browserLanguages) {
    const normalized = normalizeLocale(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return 'en';
};
